import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { userService, TCreateUserProp } from '../services/userService';
import { TUser, TUsersList, TRole } from '../context/UsersProvider';
import { handleApiError } from '../lib/errorHandler';

interface UseUsersProps {
    limit?: number;
    offset?: number;
    setSelectedProfile?: (user: TUser) => void;
    setUsersLoading: Dispatch<SetStateAction<boolean>>
    setCloseUpdateModal?: (close: boolean) => void;
    setAlertMessages?: (msgs: Partial<Record<string, string[]>>) => void;
}

export function useUserOperations(props: UseUsersProps) {
    const { limit, offset, setSelectedProfile, setUsersLoading, setCloseUpdateModal, setAlertMessages } = props;
    const [usersList, setUsersList] = useState<TUsersList>({
        count: 0,
        results: [],
    });
    const [roles, setRoles] = useState<TRole[]>([]);
    const { t } = useTranslation();

    useEffect(() => {
        if (limit !== undefined && offset !== undefined) {
            const fetchUsers = async () => {
                try {
                    setUsersLoading(true)
                    const response = await userService.getUsers(limit, offset);
                    setUsersList({
                        count: response.data.count,
                        results: response.data.results,
                    });
                } catch (error) {
                    console.error(error);
                    handleApiError(error, t('errors.users.loading'))
                } finally {
                    setUsersLoading(false)
                }
            };
            fetchUsers();
        }
    }, [limit, offset]);

    const createUser = async (data: TCreateUserProp) => {
        try {
            const response = await userService.createUser(data);
            if (response.data) {
                window.location.assign('/users');
            }
        } catch (error: any) {
            if (error.response?.data && setAlertMessages) {
                const serverError = handleApiError(error, t('errors.users.creating'), 'users');
                setAlertMessages(serverError as Partial<Record<string, string[]>>);
            }
        }
    };

    const updateUserFullName = async (id: number, firstName: string, lastName: string) => {
        if (!setSelectedProfile || !setUsersLoading || !setCloseUpdateModal || !setAlertMessages) return;

        setUsersLoading(true);
        try {
            const response = await userService.updateUserName(id, firstName, lastName);
            if (response.data) {
                setSelectedProfile(response.data);
                setCloseUpdateModal(true);
                setUsersLoading(false);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.users.updatingName'), 'users');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const updateUserPhoneNumber = async (id: number, phone: string) => {
        if (!setSelectedProfile || !setUsersLoading || !setCloseUpdateModal || !setAlertMessages) return;

        setUsersLoading(true);
        try {
            const response = await userService.updateUserPhone(id, phone);
            setSelectedProfile(response.data);
            setCloseUpdateModal(true);
            setUsersLoading(false);
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.users.updatingPhone'), 'users');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const updateUserStatus = async (id: number) => {
        if (!setSelectedProfile || !setUsersLoading || !setAlertMessages) return;

        setUsersLoading(true);
        try {
            const response = await userService.updateUserStatus(id);
            setSelectedProfile(response.data);
            setUsersLoading(false);
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.users.updatingStatus'), 'users');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const updateUserRole = async (id: number, role: string | number) => {
        if (!setSelectedProfile || !setUsersLoading || !setCloseUpdateModal || !setAlertMessages) return;

        setUsersLoading(true);
        try {
            const response = await userService.updateUserRole(id, role);
            setSelectedProfile(response.data);
            setUsersLoading(false);
            setCloseUpdateModal(true);
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.users.updatingRole'), 'users');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const getRoles = async (
        alertMessagesSetter?: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await userService.getUserRoles();
            setRoles(response.data.results);
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.users.loadingRoles'), 'users');
            if (alertMessagesSetter) {
                alertMessagesSetter(serverError as Partial<Record<string, string[]>>);
            } else if (setAlertMessages) {
                setAlertMessages(serverError as Partial<Record<string, string[]>>);
            }
        }
    };

    return {
        usersList,
        roles,
        setUsersList,
        createUser,
        updateUserFullName,
        updateUserPhoneNumber,
        updateUserStatus,
        updateUserRole,
        getRoles,
    };
}
